import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  Save,
  Download,
  Loader2,
  Plus,
  X,
} from "lucide-react";

const DEFAULT_COLUMN_LABELS = {
  particulars: "PARTICULARS",
  observation: "OBSERVATION",
  remarks: "REMARKS",
  required_value: "REQUIRED VALUE",
};

const OBSERVATION_SYMBOLS = [
  { value: "○", label: "○  No Defect" },
  { value: "×", label: "×  Defect found" },
  { value: "⊗", label: "⊗  Defect rectified" },
  { value: "/", label: "/  Not Applicable" },
];

const PACKING_OPTIONS = [
  { value: "Properly Done", label: "Properly Done" },
  { value: "Needs Attention", label: "Needs Attention" },
  { value: "Not Done", label: "Not Done" },
  { value: "/", label: "Not Applicable" },
];

const TOUCHUP_OPTIONS = [
  { value: "Not Required", label: "Not Required" },
  { value: "Done", label: "Done" },
  { value: "Pending", label: "Pending" },
  { value: "/", label: "Not Applicable" },
];

const INCLUSION_OPTIONS = [
  { value: "Included", label: "Included" },
  { value: "Not Included", label: "Not Included" },
  { value: "/", label: "Not Applicable" },
];

const INITIAL_SECTIONS = {
  sectionA: {
    title: "Section A - Mechanical Details & Test",
    backendKey: "mechanical",
    rows: [
      { sr: 1, particulars: "Arrangement of Components", observationType: "symbol", observation: "", remarks: "" },
      { sr: 2, particulars: "Controller Number", observationType: "text", observation: "", remarks: "" },
      { sr: 3, particulars: "Sensor Number", observationType: "text", observation: "", remarks: "" },
      { sr: 4, particulars: "Tag Plate Number", observationType: "text", observation: "", remarks: "" },
    ],
  },
  sectionB: {
    title: "Section B - Electrical Details & Test",
    backendKey: "electrical",
    rows: [
      { sr: 1, particulars: "Power Supply (input & output: 24V And 230V)", observationType: "symbol", observation: "", remarks: "" },
      { sr: 2, particulars: "Power On Check (Date and version)", observationType: "text", observation: "", remarks: "" },
    ],
  },
  sectionC: {
    title: "Section C - Technical Requirements",
    backendKey: "technical",
    rows: [
      { sr: 1, particulars: "Channel (single or dual)", observationType: "text", observation: "", remarks: "" },
      { sr: 2, particulars: "Sensors / Electrodes (pH, chlorine, turbidity)", observationType: "symbol", observation: "", remarks: "" },
      { sr: 3, particulars: "Outputs (1) 4-20 mA", observationType: "symbol", observation: "", remarks: "" },
      { sr: 4, particulars: "Outputs (1) RS485", observationType: "symbol", observation: "", remarks: "" },
      { sr: 5, particulars: "Check Chlorine, pH, and Turbidity in low and high Reading", observationType: "symbol", observation: "", remarks: "" },
    ],
  },
  sectionD: {
    title: "Section D - Packing Stage Checkpoints",
    backendKey: "packing",
    rows: [
      { sr: 1, particulars: "Visual Finish", observationType: "packing", observation: "", remarks: "" },
      { sr: 2, particulars: "Cleaning", observationType: "packing", observation: "", remarks: "" },
      { sr: 3, particulars: "Touch Up", observationType: "touchup", observation: "", remarks: "" },
      { sr: 4, particulars: "Sensor/Electrode Cleaning Kit", observationType: "inclusion", observation: "", remarks: "" },
      { sr: 5, particulars: "Sensor/Electrode Packing", observationType: "inclusion", observation: "", remarks: "" },
    ],
  },
};

export default function CustomQCReportPage() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [aiExtracting, setAiExtracting] = useState(false);

  const [generalDetails, setGeneralDetails] = useState({});

  const [sections, setSections] = useState(INITIAL_SECTIONS);
  const [reportTitle, setReportTitle] = useState("Inspection Report");
  const [docNo, setDocNo] = useState("");
  const [reportTemplate, setReportTemplate] = useState(null);

  const pollingRef = useRef(null);

  const mergeExtractedData = useCallback((extracted) => {
    if (!extracted) return;
    setSections((prev) => {
      const merged = JSON.parse(JSON.stringify(prev));
      const backendKeyToSection = {};
      Object.entries(merged).forEach(([k, s]) => {
        if (s.backendKey) backendKeyToSection[s.backendKey] = k;
      });
      Object.entries(extracted).forEach(([backendKey, rowMap]) => {
        const sectionKey = backendKeyToSection[backendKey];
        if (!sectionKey || !merged[sectionKey] || typeof rowMap !== "object") return;
        Object.entries(rowMap).forEach(([rowKeyStr, value]) => {
          const rows = merged[sectionKey].rows;
          const idx = parseInt(rowKeyStr, 10);
          if (!isNaN(idx) && idx < rows.length) {
            if (!rows[idx].observation) rows[idx].observation = value;
          } else {
            const target = rows.find((r) => r.rowId === rowKeyStr);
            if (target && !target.observation) target.observation = value;
          }
        });
      });
      return merged;
    });
  }, []);

  const pollExtractionStatus = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/tests/${testId}/custom-qc-report/extraction-status`,
        { headers }
      );
      if (response.data.status === "complete") {
        setAiExtracting(false);
        if (response.data.extracted_data) {
          mergeExtractedData(response.data.extracted_data);
          toast.success("AI extraction complete");
        }
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }
    } catch {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      setAiExtracting(false);
      toast.error("AI extraction failed");
    }
  }, [testId, headers, mergeExtractedData]);

  // const handleFillWithAI = useCallback(async () => {
  //   setAiExtracting(true);
  //   try {
  //     await axios.post(`${API}/tests/${testId}/custom-qc-report/extract`, {}, { headers });
  //     pollingRef.current = setInterval(pollExtractionStatus, 3000);
  //   } catch {
  //     setAiExtracting(false);
  //     toast.error("Failed to start AI extraction");
  //   }
  // }, [testId, headers, pollExtractionStatus]);

  const mapBackendToSections = useCallback((data) => {
    const VALID_SYMBOLS = ["○", "×", "⊗", "/"];
    setSections((prev) => {
      const merged = JSON.parse(JSON.stringify(prev));
      Object.entries(merged).forEach(([sectionKey, section]) => {
        const backendKey = section.backendKey;
        if (!backendKey) return;
        const items = data[backendKey];
        if (!items || !Array.isArray(items)) return;
        items.forEach((item, fallbackIdx) => {
          const idx = item.index ?? fallbackIdx;
          if (idx < section.rows.length) {
            let obs = item.observation || item.check_point_value || "";
            if (obs && !section.rows[idx].observation) {
              if (section.rows[idx].observationType === "symbol") {
                const firstSymbol = VALID_SYMBOLS.find((s) => obs.includes(s));
                obs = firstSymbol || obs;
              }
              section.rows[idx].observation = obs;
            }
            const rmk = item.remarks || "";
            if (rmk && !section.rows[idx].remarks) {
              section.rows[idx].remarks = rmk;
            }
          }
        });
      });
      return merged;
    });
  }, []);

  const buildSectionsFromTemplate = useCallback((apiSections, templateSections) => {
    const result = {};
    templateSections.forEach((sectionDef, sIdx) => {
      const key = `section${sIdx}`;
      result[key] = {
        title: sectionDef.title,
        backendKey: sectionDef.key,
        show_required_value_column: sectionDef.show_required_value_column ?? false,
        column_labels: sectionDef.column_labels || null,
        rows: (apiSections[sectionDef.key] || []).map((row, idx) => {
          const obsType = row.observationType || sectionDef.rows?.[idx]?.observationType || "symbol";
          const gc = row.grid_config || sectionDef.rows?.[idx]?.grid_config || null;
          let observation;
          if (obsType === "grid" && gc) {
            observation = Array.isArray(row.observation) && row.observation.length > 0
              ? row.observation
              : (gc.rows || []).map((r) => (r.defaults || []).map((d) => d ?? ""));
          } else {
            observation = row.observation || "";
          }
          return {
            sr: idx + 1,
            rowId: row.id || null,
            particulars: row.check_point || sectionDef.rows?.[idx]?.label || sectionDef.rows?.[idx]?.particulars || "",
            observationType: obsType,
            source: row.source || sectionDef.rows?.[idx]?.source || "step_result",
            required: Boolean(row.required ?? sectionDef.rows?.[idx]?.required),
            configured: row.configured !== undefined ? row.configured : true,
            observation,
            remarks: row.remarks || "",
            required_value: row.required_value ?? sectionDef.rows?.[idx]?.required_value ?? "",
            columns: row.columns || null,
            grid_config: gc,
            options: row.options || sectionDef.rows?.[idx]?.options || null,
          };
        }),
      };
    });
    return result;
  }, []);

  useEffect(() => {
    const prepare = async () => {
      setLoading(true);
      try {
        const response = await axios.post(
          `${API}/tests/${testId}/custom-qc-report/prepare`,
          {},
          { headers }
        );
        const data = response.data;

        if (data.general_details) {
          setGeneralDetails(data.general_details);
        }

        // Set report title if provided
        if (data.report_title) {
          setReportTitle(data.report_title);
        }

        // Check for template-driven format (sections dict + report_template)
        if (data.sections && data.report_template && data.report_template.sections) {
          setReportTemplate(data.report_template);
          if (data.report_template.doc_no) {
            setDocNo(data.report_template.doc_no);
          }
          const built = buildSectionsFromTemplate(data.sections, data.report_template.sections);
          setSections(built);
        } else {
          // Legacy format: use existing mapBackendToSections logic
          mapBackendToSections(data);
        }

        if (data.previously_saved) {
          setSaved(true);
        }
      } catch (error) {
        toast.error("Failed to prepare report data");
      } finally {
        setLoading(false);
      }
    };

    prepare();

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [testId, headers, mapBackendToSections, buildSectionsFromTemplate]);

  const updateGeneralDetail = (field, value) => {
    setGeneralDetails((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const updateSectionRow = (sectionKey, rowIndex, field, value) => {
    setSections((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated[sectionKey].rows[rowIndex][field] = value;
      return updated;
    });
    setSaved(false);
  };

  const updateColumnValue = (sectionKey, rowIndex, colKey, value) => {
    setSections((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      const row = updated[sectionKey].rows[rowIndex];
      if (typeof row.observation !== "object" || row.observation === null) {
        row.observation = {};
      }
      row.observation[colKey] = value;
      return updated;
    });
    setSaved(false);
  };

  const updateGridCell = (sectionKey, rowIndex, subRowIdx, colIdx, value) => {
    setSections((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      const row = updated[sectionKey].rows[rowIndex];
      if (!Array.isArray(row.observation)) {
        const gc = row.grid_config;
        row.observation = (gc?.rows || []).map((r) => (r.defaults || []).map((d) => d ?? ""));
      }
      if (!Array.isArray(row.observation[subRowIdx])) row.observation[subRowIdx] = [];
      row.observation[subRowIdx][colIdx] = value;
      return updated;
    });
    setSaved(false);
  };

  const addGridRow = (sectionKey, rowIndex) => {
    setSections((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      const row = updated[sectionKey].rows[rowIndex];
      const nCols = (row.grid_config?.col_headers || []).length;
      if (!Array.isArray(row.observation)) {
        row.observation = (row.grid_config?.rows || []).map((r) => (r.defaults || []).map((d) => d ?? ""));
      }
      row.observation = [...row.observation, Array(nCols).fill("")];
      row.grid_config = {
        ...row.grid_config,
        rows: [...(row.grid_config?.rows || []), { defaults: Array(nCols).fill("") }],
      };
      return updated;
    });
    setSaved(false);
  };

  const removeGridRow = (sectionKey, rowIndex, subRowIdx) => {
    setSections((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      const row = updated[sectionKey].rows[rowIndex];
      if (!Array.isArray(row.observation)) {
        row.observation = (row.grid_config?.rows || []).map((r) => (r.defaults || []).map((d) => d ?? ""));
      }
      row.observation = row.observation.filter((_, i) => i !== subRowIdx);
      row.grid_config = {
        ...row.grid_config,
        rows: (row.grid_config?.rows || []).filter((_, i) => i !== subRowIdx),
      };
      return updated;
    });
    setSaved(false);
  };

  const addSectionRow = (sectionKey) => {
    setSections((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      const rows = updated[sectionKey].rows;
      rows.push({
        sr: rows.length + 1,
        particulars: "",
        observationType: "text",
        observation: "",
        remarks: "",
        userAdded: true,
      });
      return updated;
    });
    setSaved(false);
  };

  const removeSectionRow = (sectionKey, rowIndex) => {
    setSections((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated[sectionKey].rows = updated[sectionKey].rows.filter((_, i) => i !== rowIndex);
      updated[sectionKey].rows.forEach((r, i) => { r.sr = i + 1; });
      return updated;
    });
    setSaved(false);
  };

  const buildBackendPayload = () => {
    const payload = {
      general_details: generalDetails,
    };

    if (reportTemplate) {
      // Template-driven format: send unified sections dict + metadata
      const sectionsDict = {};
      Object.entries(sections).forEach(([, section]) => {
        const beKey = section.backendKey;
        if (!beKey) return;
        sectionsDict[beKey] = (section.rows || []).map((row, idx) => {
          const item = {
            sr_no: row.sr,
            particular: row.particulars,
            check_point: row.particulars,
            observation: row.observation ?? "",
            remarks: row.remarks || "",
            required_value: row.required_value ?? "",
            observationType: row.observationType || "symbol",
            index: idx,
          };
          if (row.columns) item.columns = row.columns;
          if (row.grid_config) item.grid_config = row.grid_config;
          return item;
        });
      });
      payload.sections = sectionsDict;
      payload.report_title = reportTitle;
      payload.report_template = reportTemplate
        ? { ...reportTemplate, doc_no: docNo }
        : reportTemplate;
    } else {
      // Legacy format: top-level mechanical/electrical/technical/packing
      Object.entries(sections).forEach(([, section]) => {
        const beKey = section.backendKey;
        if (!beKey) return;
        payload[beKey] = (section.rows || []).map((row, idx) => ({
          sr_no: row.sr,
          particular: row.particulars,
          observation: row.observation || "",
          remarks: row.remarks || "",
          index: idx,
        }));
      });
    }

    return payload;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/tests/${testId}/custom-qc-report/save`,
        buildBackendPayload(),
        { headers }
      );
      setSaved(true);
      toast.success("Report saved successfully");
    } catch (error) {
      toast.error("Failed to save report");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API}/tests/${testId}/custom-qc-report/pdf`,
        { headers, responseType: "blob" }
      );

      const filename = response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || `custom_qc_report_${testId}.pdf`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const backPath = user?.role === "quality_engineer" ? "/tester/reports" : "/admin/reports";

  const renderGridSection = (row, idx, sectionKey) => {
    const gc = row.grid_config;
    const showIndex = !!gc.show_index;
    const lastColIdx = (gc.col_headers || []).length - 1;
    return (
    <div key={`grid-${sectionKey}-${idx}`} className="rounded-md border overflow-x-auto">
      {row.particulars && (
        <div className="bg-muted/60 px-3 py-2 border-b">
          <p className="text-sm font-semibold">{row.particulars}</p>
        </div>
      )}
      <table className="text-sm border-collapse w-full">
        <thead>
          <tr className="bg-slate-100">
            {showIndex && (
              <th className="border border-slate-300 px-2 py-2 text-center font-medium text-slate-600 text-xs w-8">#</th>
            )}
            {(gc.col_headers || []).map((h, cIdx) => (
              <th key={cIdx} className="border border-slate-300 px-3 py-2 text-center font-medium text-slate-600 text-xs whitespace-nowrap">{h}</th>
            ))}
            <th className="border border-slate-300 w-7"></th>
          </tr>
        </thead>
        <tbody>
          {(gc.rows || []).map((gridRow, rIdx) => {
            const obsArr = Array.isArray(row.observation) ? row.observation : [];
            const rowVals = Array.isArray(obsArr[rIdx]) ? obsArr[rIdx] : [];
            return (
              <tr key={rIdx} className={rIdx % 2 === 1 ? "bg-slate-50" : ""}>
                {showIndex && (
                  <td className="border border-slate-300 px-2 py-1 text-center text-xs text-slate-500">{rIdx + 1}</td>
                )}
                {(gridRow.defaults || []).map((defVal, cIdx) => {
                  const cellVal = rowVals[cIdx] !== undefined ? rowVals[cIdx] : (defVal ?? "");
                  const isConfiguredCell = gc.configure_columns && cIdx === lastColIdx;
                  const colType = isConfiguredCell ? (gridRow.cell_type || "text") : "text";
                  const colSource = isConfiguredCell ? (gridRow.cell_source || "manual") : "manual";
                  const isStatic = colSource === "static";
                  if (colType === "symbol") {
                    return (
                      <td key={cIdx} className="border border-slate-300 px-1 py-0.5">
                        <Select
                          value={cellVal}
                          disabled={isStatic}
                          onValueChange={(val) => updateGridCell(sectionKey, idx, rIdx, cIdx, val)}
                        >
                          <SelectTrigger className={`h-7 text-xs border-0 ${isStatic ? "bg-muted text-muted-foreground" : "bg-transparent"}`}>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {OBSERVATION_SYMBOLS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    );
                  }
                  return (
                    <td key={cIdx} className="border border-slate-300 px-1 py-0.5">
                      <Input
                        value={cellVal}
                        disabled={isStatic}
                        onChange={(e) => !isStatic && updateGridCell(sectionKey, idx, rIdx, cIdx, e.target.value)}
                        className={`h-7 text-xs border-0 p-1 w-full min-w-[70px] text-center ${isStatic ? "bg-muted text-muted-foreground" : "bg-transparent"}`}
                        placeholder="—"
                      />
                    </td>
                  );
                })}
                <td className="border border-slate-300 px-1 py-0.5 w-7">
                  <button
                    type="button"
                    onClick={() => removeGridRow(sectionKey, idx, rIdx)}
                    className="w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:text-red-500 hover:bg-red-50"
                    title="Remove row"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-3 py-2 border-t">
        <button
          type="button"
          onClick={() => addGridRow(sectionKey, idx)}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"
        >
          <Plus className="w-3 h-3" /> Add row
        </button>
      </div>
    </div>
    );
  };

  const renderSectionTable = (sectionKey, section) => {
    const regularRows = section.rows.filter((r) => r.observationType !== "grid");
    const gridRows = section.rows.filter((r) => r.observationType === "grid" && r.grid_config);
    const showReqVal = !!section.show_required_value_column;
    const colLabels = { ...DEFAULT_COLUMN_LABELS, ...(section.column_labels || {}) };

    return (
    <Card key={sectionKey}>
      {section.title && (
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{section.title}</CardTitle>
        </CardHeader>
      )}
      <CardContent className={`space-y-4 ${!section.title ? "pt-4" : "pt-0"}`}>
        {regularRows.length > 0 && (
        <div className="rounded-md border overflow-x-auto">
          <Table className="table-fixed w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[56px]">SR.</TableHead>
                <TableHead className="w-[25%]">{colLabels.particulars}</TableHead>
                {showReqVal && <TableHead className="w-[15%]">{colLabels.required_value}</TableHead>}
                <TableHead>{colLabels.observation}</TableHead>
                <TableHead className="w-[22%]">{colLabels.remarks}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {regularRows.map((row, i) => {
                const idx = section.rows.indexOf(row);
                const isMissing = row.required && row.configured === false;
                return (
                <TableRow key={`${sectionKey}-${i}`} className={isMissing ? "bg-amber-50" : ""}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-1">
                      {row.sr}
                      {row.userAdded && (
                        <button
                          type="button"
                          onClick={() => removeSectionRow(sectionKey, idx)}
                          className="w-4 h-4 flex items-center justify-center rounded text-slate-400 hover:text-red-500 hover:bg-red-50"
                          title="Remove row"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      {row.userAdded ? (
                        <Input
                          value={row.particulars}
                          onChange={(e) => updateSectionRow(sectionKey, idx, "particulars", e.target.value)}
                          placeholder="Description"
                          className="w-full h-8 text-sm"
                        />
                      ) : (
                        <>
                          {row.particulars || <span className="text-muted-foreground">—</span>}
                          {isMissing && <span className="text-xs text-amber-600 font-medium">• not configured</span>}
                        </>
                      )}
                    </div>
                  </TableCell>
                  {showReqVal && (
                  <TableCell>
                    {row.required_value !== undefined && row.required_value !== "" ? (
                      <span className="text-sm">{row.required_value}</span>
                    ) : (
                      <Input
                        value={row.required_value ?? ""}
                        onChange={(e) => updateSectionRow(sectionKey, idx, "required_value", e.target.value)}
                        placeholder="—"
                        className="w-full h-8 text-sm"
                      />
                    )}
                  </TableCell>
                  )}
                  <TableCell colSpan={row.columns ? 2 : 1}>
                    {row.columns ? (
                      <div className="flex flex-wrap gap-3">
                        {row.columns.map((col) => {
                          const obsObj = typeof row.observation === "object" && row.observation !== null ? row.observation : {};
                          const colVal = obsObj[col.key] ?? (col.static_value ?? "");
                          const isStatic = col.static_value !== undefined;
                          return (
                            <div key={col.key} className="flex flex-col gap-1 min-w-[90px]">
                              <span className="text-xs text-muted-foreground leading-tight">{col.label}</span>
                              <Input
                                value={colVal}
                                disabled={isStatic}
                                onChange={(e) => !isStatic && updateColumnValue(sectionKey, idx, col.key, e.target.value)}
                                placeholder={isStatic ? "" : "Enter"}
                                className={`text-sm h-8 ${isStatic ? "bg-muted text-muted-foreground" : ""}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ) : ["symbol", "packing", "touchup", "inclusion"].includes(row.observationType) ? (
                      <Select
                        value={row.observation}
                        onValueChange={(val) => updateSectionRow(sectionKey, idx, "observation", val)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {(row.observationType === "packing" ? PACKING_OPTIONS :
                            row.observationType === "touchup" ? TOUCHUP_OPTIONS :
                            row.observationType === "inclusion" ? INCLUSION_OPTIONS :
                            OBSERVATION_SYMBOLS
                          ).map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : row.options?.length > 0 ? (
                      <Select
                        value={row.observation}
                        onValueChange={(val) => updateSectionRow(sectionKey, idx, "observation", val)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select…" />
                        </SelectTrigger>
                        <SelectContent>
                          {row.options.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={row.observation}
                        onChange={(e) => updateSectionRow(sectionKey, idx, "observation", e.target.value)}
                        placeholder="Enter value"
                        className="w-full"
                      />
                    )}
                  </TableCell>
                  {!row.columns && (
                  <TableCell>
                    <Input
                      value={row.remarks}
                      onChange={(e) => updateSectionRow(sectionKey, idx, "remarks", e.target.value)}
                      placeholder="Remarks"
                      className="w-full"
                    />
                  </TableCell>
                  )}
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <div className="px-3 py-2 border-t">
            <button
              type="button"
              onClick={() => addSectionRow(sectionKey)}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"
            >
              <Plus className="w-3 h-3" /> Add row
            </button>
          </div>
        </div>
        )}

        {gridRows.map((row) => {
          const idx = section.rows.indexOf(row);
          return renderGridSection(row, idx, sectionKey);
        })}
      </CardContent>
    </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(backPath)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Custom QC Report</h2>
          </div>
        </div>
        {/* <Button
          variant="outline"
          onClick={handleFillWithAI}
          disabled={aiExtracting}
          className="gap-2"
        >
          {aiExtracting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Bot className="w-4 h-4" />
          )}
          {aiExtracting ? "Extracting..." : "Fill with AI"}
        </Button> */}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Report Info</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="report-title">Report Title</Label>
              <Input
                id="report-title"
                value={reportTitle}
                onChange={(e) => { setReportTitle(e.target.value); setSaved(false); }}
                placeholder="e.g. SGC1 Sample Gas Cooler - Quality Test Report"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="doc-no">Doc No.</Label>
              <Input
                id="doc-no"
                value={docNo}
                onChange={(e) => { setDocNo(e.target.value); setSaved(false); }}
                placeholder="e.g. F/QC/8.0/41, Rev.01"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {(() => {
        const gdFields = reportTemplate?.general_detail_fields;
        if (Array.isArray(gdFields) && gdFields.length === 0) return null;
        const fields = gdFields || [
          { key: "qc_engineer",         label: "QC Engineer",                required: false },
          { key: "date_of_inspection",  label: "Date of Inspection",          required: false },
          { key: "customer_name",       label: "Customer Name",               required: true  },
          { key: "customer_po_wo",      label: "Customer PO / WO No & Date", required: true  },
          { key: "asl_so_no",           label: "ASL SO No. & Date",           required: true  },
          { key: "enclosure_serial_no", label: "Enclosure Serial No.",        required: false },
        ];
        return (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">General Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {fields.map((field) => (
                  <div key={field.key} className="space-y-2">
                    <Label htmlFor={field.key}>{field.label}</Label>
                    <Input
                      id={field.key}
                      type={field.key === "date_of_inspection" ? "date" : "text"}
                      value={generalDetails[field.key] ?? field.default_value ?? ""}
                      onChange={(e) => updateGeneralDetail(field.key, e.target.value)}
                      placeholder={field.default_value || ""}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {Object.entries(sections).map(([key, section]) => renderSectionTable(key, section))}

      <Card className="bg-muted/50">
        <CardContent className="py-4">
          <p className="text-sm font-medium mb-2">Legend</p>
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="flex items-center gap-1.5"><span className="text-green-700 font-semibold">○</span> No Defect (Pass)</span>
            <span className="flex items-center gap-1.5"><span className="text-red-700 font-semibold">×</span> Defect Found (Fail)</span>
            <span className="flex items-center gap-1.5"><span className="text-yellow-700 font-semibold">△</span> Warning / Needs Review</span>
            <span className="flex items-center gap-1.5"><span className="font-semibold">⊗</span> Defect Rectified</span>
            <span className="flex items-center gap-1.5"><span className="font-semibold">/</span> Not Applicable</span>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-3 pb-6">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save
        </Button>
        <Button variant="outline" onClick={handleDownloadPdf} disabled={!saved || downloading}>
          {downloading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          Download PDF
        </Button>
      </div>
    </div>
  );
}
